import { defineStyle, defineStyleConfig, ComponentStyleConfig } from '@chakra-ui/react';
import { transparentize } from '@chakra-ui/theme-tools'; // transparentize appears to be replaced with colorMix in chakra v3, currently still in development

const sizes = {
  xs: {
    fontSize: 'xs',
    lineHeight: 4,
  },
  sm: {
    fontSize: 'sm',
    lineHeight: 4,
  },
  md: {
    fontSize: 'md',
    lineHeight: 5,
  },
  lg: {
    fontSize: 'lg',
    lineHeight: 6,
  },
};

// These are tricky to meet WCAG contrast ratios
const trickyColors = ['yellow', 'orange', 'cyan', 'teal', 'green'];

const accessibleColors = defineStyle(props => {
  const { colorScheme } = props;
  const isTricky = trickyColors.includes(colorScheme);
  const color = isTricky ? `${colorScheme}.700` : `${colorScheme}.600`;

  return {
    color,
  };
});

const colorfulIcons = defineStyle(props => {
  const { colorScheme } = props;

  return {
    '.colorful-icon': {
      color: `${colorScheme}.500`,

      _dark: {
        color: `${colorScheme}.400`,
      },
    },
  };
});

const variantSecondary = defineStyle(props => {
  const { colorScheme, theme } = props;

  return {
    bgColor: 'utility.100',
    ...accessibleColors(props),
    ...colorfulIcons(props),

    _hover: {
      color: `${colorScheme}.700`,
      bgColor: `${colorScheme}.100`,

      _disabled: {
        bgColor: 'utility.100',
      },
    },

    _active: {
      color: `${colorScheme}.800`,
      bgColor: `${colorScheme}.200`,
    },

    _dark: {
      color: `${colorScheme}.300`,
      bgColor: 'whiteAlpha.100',

      _hover: {
        color: `${colorScheme}.200`,
        bgColor: transparentize(`${colorScheme}.200`, 0.16)(theme),

        _disabled: {
          bgColor: transparentize(`${colorScheme}.200`, 0.16)(theme),
        },
      },

      _active: {
        color: `${colorScheme}.100`,
        bgColor: transparentize(`${colorScheme}.200`, 0.32)(theme),
      },
    },
  };
});

const variantPrimary = defineStyle(props => {
  const { colorScheme } = props;
  const isTricky = trickyColors.includes(colorScheme);

  return {
    color: isTricky ? `${colorScheme}.900` : 'white',
    bgColor: isTricky ? `${colorScheme}.400` : `${colorScheme}.500`,

    _hover: {
      bgColor: isTricky ? `${colorScheme}.300` : `${colorScheme}.600`,
    },

    _active: {
      bgColor: isTricky ? `${colorScheme}.200` : `${colorScheme}.700`,
    },

    '.utility-icon': {
      color: isTricky ? `${colorScheme}.800` : 'whiteAlpha.800',
    },

    _dark: {
      bgColor: `${colorScheme}.300`,
      color: `${colorScheme}.900`,

      _hover: {
        bgColor: `${colorScheme}.200`,
      },

      _active: {
        bgColor: `${colorScheme}.100`,
      },

      '.utility-icon': {
        color: 'utility.600',
      },
    },

    _light: {
      _focusVisible: {
        boxShadow: 'outlineInverse',
      },
    },
  };
});

const variantTertiary = defineStyle(props => {
  const { colorScheme, theme } = props;

  return {
    ...accessibleColors(props),
    ...colorfulIcons(props),

    _hover: {
      color: `${colorScheme}.700`,
      bgColor: `${colorScheme}.100`,
    },

    _active: {
      color: `${colorScheme}.800`,
      bgColor: `${colorScheme}.200`,
    },

    _dark: {
      color: `${colorScheme}.300`,

      _hover: {
        color: `${colorScheme}.200`,
        bgColor: transparentize(`${colorScheme}.200`, 0.16)(theme),

        _disabled: {
          bgColor: 'initial',
        },
      },

      _active: {
        color: `${colorScheme}.100`,
        bgColor: transparentize(`${colorScheme}.200`, 0.32)(theme),
      },
    },
  };
});

const variantOutline = defineStyle(props => {
  const { colorScheme } = props;
  const isTricky = trickyColors.includes(colorScheme);

  return {
    ...accessibleColors(props),
    ...colorfulIcons(props),
    borderColor: isTricky ? `${colorScheme}.400` : `${colorScheme}.300`,

    _dark: {
      borderColor: `${colorScheme}.600`,
      color: `${colorScheme}.300`,

      _hover: {
        borderColor: `${colorScheme}.500`,
        color: `${colorScheme}.200`,

        _disabled: {
          bgColor: 'initial',
        },
      },

      _active: {
        borderColor: `${colorScheme}.400`,
        color: `${colorScheme}.100`,
      },
    },
  };
});

const variantLink = defineStyle(props => {
  const { colorScheme } = props;
  return {
    ...accessibleColors(props),

    _dark: {
      color: `${colorScheme}.300`,
    },

    _hover: {
      color: `${colorScheme}.700`,

      _dark: {
        color: `${colorScheme}.200`,
      },
    },

    _active: {
      color: `${colorScheme}.800`,

      _dark: {
        color: `${colorScheme}.100`,
      },
    },
  };
});

const variantTopNav = defineStyle({
  bgColor: 'transparent',
  borderRadius: 0,
  color: 'whiteAlpha.800',
  flexDirection: 'column',
  fontSize: 'xs',
  height: 'auto',
  position: 'relative',
  pt: 1,
  pb: 2,
  px: 0,
  width: '100%',

  '.chakra-button__icon': {
    mx: 0,
  },

  '.nav-icon': {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '1.25rem',
    width: '2.5rem',
    height: '2.5rem',
    mx: 'auto',
    flexShrink: 0,
    borderRadius: 'xl',
    transitionProperty: 'background-color, color',
    transitionTimingFunction: 'ease-in-out',
    transitionDuration: '0.2s',

    svg: {
      display: 'block',
    },
  },

  '.utility-icon': {
    color: 'whiteAlpha.600',
    fontSize: 'sm',
    position: 'absolute',
    right: 2,
    top: 4,

    transitionProperty: 'right, color',
    transitionTimingFunction: 'ease-in-out',
    transitionDuration: '0.2s',

    svg: {
      display: 'block',
    },
  },

  _hover: {
    color: 'whiteAlpha.900',

    '.nav-icon': {
      bgColor: 'whiteAlpha.200',
    },

    '.utility-icon': {
      right: 1,
      color: 'whiteAlpha.800',
    },
  },

  _active: {
    color: 'whiteAlpha.900',

    '.nav-icon': {
      bgColor: 'blackAlpha.400',
      color: 'yellow.400',

      _dark: {
        bgColor: 'blackAlpha.500',
      },
    },

    '.utility-icon': {
      right: 1,
      color: 'whiteAlpha.800',
    },
  },

  _light: {
    _focusVisible: {
      boxShadow: 'outlineInverse',
    },
  },
});

const variantSubnav = defineStyle({
  color: 'utility.800',
  display: 'flex',
  justifyContent: 'start',
  textAlign: 'left',
  width: '100%',

  '.nav-icon': {
    color: 'utility.500',
  },

  _hover: {
    bgColor: 'utility.100',
    color: 'utility.900',
  },

  _active: {
    bgColor: 'blue.200',
    color: 'utility.900',

    '.nav-icon': {
      color: 'blue.500',
    },
  },

  _dark: {
    color: 'whiteAlpha.800',

    '.nav-icon': {
      color: 'whiteAlpha.500',
    },

    _hover: {
      bgColor: 'whiteAlpha.100',
      color: 'whiteAlpha.900',

      _disabled: {
        bgColor: 'initial',
        color: 'whiteAlpha.700',
      },
    },

    _active: {
      bgColor: 'whiteAlpha.200',
      color: 'whiteAlpha.900',

      '.nav-icon': {
        color: 'whiteAlpha.700',
      },
    },
  },
});

const variantSidebar = defineStyle(props => {
  const { colorScheme } = props;

  return {
    ...variantSubnav,
    justifyContent: 'space-between',
    textAlign: 'left',
    px: 'sidebarPxSmall',
    minHeight: { base: 9, md: 0 },

    _hover: {
      bgColor: `${colorScheme}.100`,
      color: `${colorScheme}.800`,
    },

    _active: {
      bgColor: `${colorScheme}.200`,
      color: `${colorScheme}.900`,
    },
  };
});

const variantQuickLink = defineStyle(props => {
  const { colorScheme } = props;
  return {
    ...variantSubnav,

    _hover: {
      bgColor: `${colorScheme}.50`,
    },

    _active: {
      bgColor: `${colorScheme}.100`,
      color: `${colorScheme}.900`,
    },
  };
});

const variantMobileTopNav = defineStyle({
  ...variantSubnav,
  fontSize: 'sm',
  letterSpacing: 'wider',
  textTransform: 'uppercase',

  '.nav-icon': {
    color: 'utility-icon',
    mr: 2,
  },

  _hover: {
    bgColor: 'blue.50',
  },

  _active: {
    bgColor: 'blue.900',
    color: 'white',

    '.nav-icon': {
      color: 'yellow.400',
    },
  },
});

const variantStatus = defineStyle(props => {
  const { colorScheme, theme } = props;

  return {
    color: `${colorScheme}.800`,
    bgColor: `${colorScheme}.100`,
    ...colorfulIcons(props),

    '.colorful-icon': {
      color: `${colorScheme}.500`,
    },

    _hover: {
      color: `${colorScheme}.900`,
      bgColor: `${colorScheme}.200`,

      _disabled: {
        opacity: 0.6,
      },
    },

    _active: {
      color: `${colorScheme}.900`,
      bgColor: `${colorScheme}.300`,
    },

    _dark: {
      color: `${colorScheme}.300`,
      bgColor: transparentize(`${colorScheme}.100`, 0.16)(theme),

      '.colorful-icon': {
        color: `${colorScheme}.300`,
      },

      _hover: {
        color: `${colorScheme}.200`,
        bgColor: transparentize(`${colorScheme}.200`, 0.16)(theme),

        _disabled: {
          bgColor: transparentize(`${colorScheme}.200`, 0.16)(theme),
        },
      },

      _active: {
        color: `${colorScheme}.100`,
        bgColor: transparentize(`${colorScheme}.200`, 0.32)(theme),
      },
    },
  };
});

export const buttonTheme: ComponentStyleConfig = defineStyleConfig({
  baseStyle: {
    borderRadius: 'lg',
    flexShrink: '0',
    fontWeight: 'medium',
    letterSpacing: 'tight',

    _hover: {
      textDecoration: 'none',
    },

    _disabled: {
      opacity: 0.65,
    },
  },
  defaultProps: {
    colorScheme: 'blue',
    variant: 'secondary',
  },
  sizes,
  variants: {
    solid: variantPrimary,
    secondary: variantSecondary,
    ghost: variantTertiary,
    outline: variantOutline,
    link: variantLink,
    topnav: variantTopNav,
    mobiletopnav: variantMobileTopNav,
    subnav: variantSubnav,
    sidebar: variantSidebar,
    quicklink: variantQuickLink,
    status: variantStatus,
  },
});
